import React from 'react';

import type { Node, Edge } from '../../../core/models/scene';
import type { OrgReportCounts } from '../../../domains/orgchart/services/orgChartReportCounts';

export { ORG_NODE_TYPES } from '../../../domains/orgchart/constants/orgNodeTypes';

export type OrgChartDeps = {
  UpdateOrgDepartmentCommand: new (...args: any[]) => any;
  UpdateOrgPersonCommand: new (...args: any[]) => any;
  UpdateOrgPositionCommand: new (...args: any[]) => any;
  CreateOrgDepartmentCommand: new (...args: any[]) => any;
  CreateOrgPersonCommand: new (...args: any[]) => any;
  CreateOrgPositionCommand: new (...args: any[]) => any;
  CreateReportsToCommand: new (...args: any[]) => any;
  CreateMatrixReportsCommand: new (...args: any[]) => any;
  CreateOccupiesCommand: new (...args: any[]) => any;
  OrgChartPerspectiveProjection: { compute: (...args: any[]) => { nodes: Node[]; edges: Edge[]; tenure?: any } };
  applyOrgChartCollapse: (nodes: Node[], edges: Edge[], collapsed: string[]) => { nodes: Node[]; edges: Edge[] };
  computeFocusProjection: (nodes: readonly Node[], edges: readonly Edge[], scope: any, activeHeatmapField?: string) => any;
  computeOrgReportCounts: (nodes: Node[], edges: Edge[]) => Map<string, OrgReportCounts>;
  computeOrgReportingTargets: (nodes: Node[], edges: Edge[]) => Map<string, string>;
  markNodeDirty: (nodeId: string) => void;
  useAutoLayout: () => { markDirty: (nodeId: string) => void; enabled: boolean; applyLayout: any };
  computeProjectedScenarioView: (
    baseNodes: Node[],
    baseEdges: Edge[],
    activeScenarioId: unknown,
    scenarioState: unknown,
    activeScenario: { delta: any; name: string } | null,
    effectiveCollapsedIds?: string[],
  ) => {
    nodes: Node[];
    edges: Edge[];
    statusIndex: any;
    isScenarioActive: boolean;
    scenarioName: string | null;
    isEditingScenario: boolean;
  };
};

let orgChartDepsPromise: Promise<OrgChartDeps> | null = null;

export const loadOrgChartDeps = (): Promise<OrgChartDeps> => {
  if (!orgChartDepsPromise) {
    orgChartDepsPromise = (async () => {
      const [
        commands,
        createOrgDepartment,
        createOrgPerson,
        createOrgPosition,
        createReportsTo,
        createMatrixReports,
        createOccupies,
        perspectiveProjection,
        collapseProjection,
        focusProjectionModule,
        reportCounts,
        autoLayout,
        projectedScenario,
      ] = await Promise.all([
        import('../../../domains/orgchart/commands'),
        import('../../../domains/orgchart/commands/CreateOrgDepartmentCommand'),
        import('../../../domains/orgchart/commands/CreateOrgPersonCommand'),
        import('../../../domains/orgchart/commands/CreateOrgPositionCommand'),
        import('../../../domains/orgchart/commands/CreateReportsToCommand'),
        import('../../../domains/orgchart/commands/CreateMatrixReportsCommand'),
        import('../../../domains/orgchart/commands/CreateOccupiesCommand'),
        import('../../../domains/orgchart/services/OrgChartPerspectiveProjection'),
        import('../../../domains/orgchart/services/orgChartCollapseProjection'),
        import('../../../domains/orgchart/services/focusProjection'),
        import('../../../domains/orgchart/services/orgChartReportCounts'),
        import('../../../domains/orgchart/hooks/useAutoLayout'),
        import('../../../domains/orgchart/hooks/useProjectedScenarioView'),
      ]);

      return {
        UpdateOrgDepartmentCommand: commands.UpdateOrgDepartmentCommand,
        UpdateOrgPersonCommand: commands.UpdateOrgPersonCommand,
        UpdateOrgPositionCommand: commands.UpdateOrgPositionCommand,
        CreateOrgDepartmentCommand: createOrgDepartment.CreateOrgDepartmentCommand,
        CreateOrgPersonCommand: createOrgPerson.CreateOrgPersonCommand,
        CreateOrgPositionCommand: createOrgPosition.CreateOrgPositionCommand,
        CreateReportsToCommand: createReportsTo.CreateReportsToCommand,
        CreateMatrixReportsCommand: createMatrixReports.CreateMatrixReportsCommand,
        CreateOccupiesCommand: createOccupies.CreateOccupiesCommand,
        OrgChartPerspectiveProjection: perspectiveProjection.OrgChartPerspectiveProjection,
        applyOrgChartCollapse: collapseProjection.applyOrgChartCollapse,
        computeFocusProjection: focusProjectionModule.computeFocusProjection,
        computeOrgReportCounts: reportCounts.computeOrgReportCounts,
        computeOrgReportingTargets: reportCounts.computeOrgReportingTargets,
        markNodeDirty: autoLayout.markNodeDirty,
        useAutoLayout: autoLayout.useAutoLayout,
        computeProjectedScenarioView: projectedScenario.computeProjectedScenarioView,
      } satisfies OrgChartDeps;
    })();
  }

  return orgChartDepsPromise as Promise<OrgChartDeps>;
};

export const OrgAutoLayoutProvider: React.FC<{ useAutoLayout: OrgChartDeps['useAutoLayout'] }> = ({
  useAutoLayout,
}) => {
  useAutoLayout();
  return null;
};
